export const BILLING_GRACE_MS = 7 * 24 * 60 * 60 * 1000;
export const TRIAL_REMIND_MS = 7 * 24 * 60 * 60 * 1000;

const PAID_STRIPE = new Set(['active', 'trialing']);

export function formatPenceGbp(pence) {
  if (pence == null || Number.isNaN(Number(pence))) return 'Not set';
  const pounds = Number(pence) / 100;
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pounds);
}

export function penceFromPoundsInput(value) {
  const n = Number(String(value).replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

function asDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Derive billing UI state from organisation fields.
 * Orgs with no trial_ends_at and no Stripe subscription are left open (legacy).
 */
function withOverride(state, override) {
  return { ...state, accessOverride: override };
}

/**
 * Whether staff should be refused at login (and shown a modal).
 * Super Admins are never blocked by this helper — callers must skip them.
 */
export function getOrgLoginBlock(org) {
  if (!org) return null;

  if (org.is_active === false) {
    return {
      reason: 'deactivated',
      title: 'This organisation is no longer active',
      message: 'Sign-in has been turned off for your organisation. Your password is fine — the workspace itself is closed for now.',
    };
  }

  if (String(org.billing_access_override || '').toLowerCase() === 'locked') {
    return {
      reason: 'locked',
      title: 'Your organisation has been locked',
      message: 'MAAP has temporarily paused sign-in for this organisation. Nobody on your team can open the apps until access is restored.',
    };
  }

  const state = deriveBillingState(org);
  if (state.phase === 'deactivated') {
    return {
      reason: 'deactivated',
      title: 'This organisation is no longer active',
      message: 'Sign-in has been turned off for your organisation. Your password is fine — the workspace itself is closed for now.',
    };
  }
  if (state.blocking && state.phase === 'locked') {
    return {
      reason: 'billing_locked',
      title: 'Your organisation’s access has ended',
      message: 'The free trial or subscription for this organisation has finished, so the workspace is closed until billing is up to date.',
    };
  }

  return null;
}

export function orgLoginBlockedReason(org) {
  return getOrgLoginBlock(org)?.message || null;
}

export function deriveBillingState(org, now = new Date()) {
  if (!org) {
    return { phase: 'none', blocking: false, remind: false, trialEndsAt: null, graceEndsAt: null, accessOverride: 'auto' };
  }

  const trialEnd = asDate(org.trial_ends_at);
  const graceEndsAt = asDate(org.billing_grace_ends_at)
    || (trialEnd ? new Date(trialEnd.getTime() + BILLING_GRACE_MS) : null);
  const override = String(org.billing_access_override || 'auto').toLowerCase();

  if (org.is_active === false) {
    return withOverride({ phase: 'deactivated', blocking: true, remind: false, trialEndsAt: trialEnd, graceEndsAt }, override);
  }
  if (override === 'locked') {
    return withOverride({ phase: 'locked', blocking: true, remind: true, trialEndsAt: trialEnd, graceEndsAt }, override);
  }
  if (override === 'unlocked') {
    return withOverride({ phase: 'unlocked', blocking: false, remind: false, trialEndsAt: trialEnd, graceEndsAt }, override);
  }
  if (override === 'complimentary') {
    return withOverride({ phase: 'complimentary', blocking: false, remind: false, trialEndsAt: trialEnd, graceEndsAt }, override);
  }

  const stripeStatus = org.stripe_subscription_status || '';
  if (PAID_STRIPE.has(stripeStatus)) {
    return withOverride({
      phase: 'active',
      blocking: false,
      remind: false,
      trialEndsAt: asDate(org.trial_ends_at),
      graceEndsAt: asDate(org.billing_grace_ends_at),
    }, override);
  }

  if (stripeStatus === 'past_due' || stripeStatus === 'unpaid') {
    const overdueGraceEndsAt = asDate(org.billing_grace_ends_at);
    const graceExpired = overdueGraceEndsAt && now >= overdueGraceEndsAt;
    if (graceExpired || org.billing_locked_at) {
      return withOverride({
        phase: 'locked',
        blocking: true,
        remind: true,
        trialEndsAt: trialEnd,
        graceEndsAt: overdueGraceEndsAt,
      }, override);
    }
    return withOverride({
      phase: 'past_due',
      blocking: false,
      remind: true,
      trialEndsAt: trialEnd,
      graceEndsAt: overdueGraceEndsAt,
    }, override);
  }

  if (!trialEnd && !org.billing_locked_at) {
    return withOverride({ phase: 'none', blocking: false, remind: false, trialEndsAt: null, graceEndsAt: null }, override);
  }

  if (org.billing_locked_at) {
    return withOverride({ phase: 'locked', blocking: true, remind: true, trialEndsAt: trialEnd, graceEndsAt }, override);
  }

  if (trialEnd && now < trialEnd) {
    const remind = trialEnd.getTime() - now.getTime() <= TRIAL_REMIND_MS;
    return withOverride({ phase: remind ? 'trial_ending' : 'trial', blocking: false, remind, trialEndsAt: trialEnd, graceEndsAt }, override);
  }

  if (graceEndsAt && now < graceEndsAt) {
    return withOverride({ phase: 'grace', blocking: false, remind: true, trialEndsAt: trialEnd, graceEndsAt }, override);
  }

  if (trialEnd || graceEndsAt) {
    return withOverride({ phase: 'locked', blocking: true, remind: true, trialEndsAt: trialEnd, graceEndsAt }, override);
  }

  return withOverride({ phase: 'none', blocking: false, remind: false, trialEndsAt: trialEnd, graceEndsAt }, override);
}

export function billingPhaseLabel(phase) {
  switch (phase) {
    case 'active': return 'Paying';
    case 'trial': return 'Trial';
    case 'trial_ending': return 'Trial ending';
    case 'grace': return 'Grace period';
    case 'past_due': return 'Payment failed';
    case 'locked': return 'Locked';
    case 'unlocked': return 'Manually unlocked';
    case 'complimentary': return 'Complimentary';
    case 'deactivated': return 'Deactivated';
    case 'pending_setup': return 'Pending setup';
    default: return 'Not billed';
  }
}

/** Customer-facing labels — avoid internal terms like “grace period”. */
export function customerBillingPhaseLabel(phase) {
  switch (phase) {
    case 'active': return 'Active';
    case 'trial': return 'Free trial';
    case 'trial_ending': return 'Trial ending soon';
    case 'grace': return 'Billing needed';
    case 'past_due': return 'Payment failed';
    case 'locked': return 'Access paused';
    case 'unlocked': return 'Access open';
    case 'complimentary': return 'Complimentary';
    case 'deactivated': return 'Deactivated';
    case 'pending_setup': return 'Setting up';
    default: return 'Not set up';
  }
}

export function billingPhaseTone(phase) {
  switch (phase) {
    case 'active':
      return { badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', bar: 'bg-emerald-500' };
    case 'trial':
      return { badge: 'bg-sky-50 text-sky-700 border-sky-200', dot: 'bg-sky-500', bar: 'bg-sky-500' };
    case 'trial_ending':
      return { badge: 'bg-amber-50 text-amber-800 border-amber-200', dot: 'bg-amber-500', bar: 'bg-amber-500' };
    case 'grace':
      return { badge: 'bg-orange-50 text-orange-800 border-orange-200', dot: 'bg-orange-500', bar: 'bg-orange-500' };
    case 'past_due':
      return { badge: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500', bar: 'bg-rose-500' };
    case 'locked':
      return { badge: 'bg-slate-100 text-slate-700 border-slate-300', dot: 'bg-slate-500', bar: 'bg-slate-500' };
    case 'unlocked':
      return { badge: 'bg-teal-50 text-teal-800 border-teal-200', dot: 'bg-teal-500', bar: 'bg-teal-500' };
    case 'complimentary':
      return { badge: 'bg-orange-50 text-orange-800 border-orange-200', dot: 'bg-orange-500', bar: 'bg-orange-500' };
    case 'deactivated':
      return { badge: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500', bar: 'bg-red-500' };
    case 'pending_setup':
      return { badge: 'bg-amber-50 text-amber-800 border-amber-200', dot: 'bg-amber-500', bar: 'bg-amber-400' };
    default:
      return { badge: 'bg-surface text-muted-foreground border-border', dot: 'bg-slate-400', bar: 'bg-slate-300' };
  }
}

export function formatBillingDate(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function daysUntil(value, now = new Date()) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
}

export function billedHomeCount(org) {
  return org?.billing_home_count_override
    || org?.live_home_count
    || org?.billed_care_home_count
    || 0;
}

/** True when the org is on the bed-size catalogue (not legacy per-home billing). */
export function isBedTierBilling(org) {
  return Boolean(
    org?.billing_tier_id
    || org?.billing_included_users != null
    || org?.billing_bed_count != null
    || org?.billing_plan_mode === 'group'
    || org?.billing_plan_mode === 'custom',
  );
}

export function orgSeatLimit(org) {
  if (!org) return null;
  if (org.billing_included_users == null && org.max_users == null) return null;
  return Math.max(
    0,
    (Number(org.billing_included_users) || Number(org.max_users) || 0)
      + (Number(org.billing_extra_seats) || 0),
  );
}

export function monthlyPenceForOrg(org, defaultUnitAmountPence = null, extraUserPence = 500) {
  const extraRate = org?.billing_extra_user_pence ?? extraUserPence;
  const extras = (Number(org?.billing_extra_seats) || 0) * Number(extraRate || 0);

  if (org?.billing_plan_mode === 'group' || org?.billing_plan_mode === 'custom') {
    const base = org.billing_custom_monthly_pence ?? org.billing_unit_amount_pence ?? defaultUnitAmountPence;
    if (base == null) return null;
    return Number(base) + extras;
  }

  if (isBedTierBilling(org)) {
    const base = org?.billing_unit_amount_pence ?? defaultUnitAmountPence;
    if (base == null) return null;
    return Number(base) + extras;
  }

  const unit = org?.billing_unit_amount_pence ?? defaultUnitAmountPence;
  if (unit == null) return null;
  const homes = Math.max(1, billedHomeCount(org) || 1);
  return unit * homes;
}

export function planModeLabel(mode) {
  switch (String(mode || 'standard')) {
    case 'group': return 'Group plan';
    case 'custom': return 'Custom plan';
    default: return 'Bed-size plan';
  }
}
